'use client';

import React, { useState } from 'react';
import { Separator, copyToast } from '@skin-seoul/design-system';
import { type TrackingEvent } from '@skin-seoul/service';
import { LucideIcon } from '../LucideIcon';
import { TrackingTimeline } from './TrackingTimeline';

interface OrderTrackingInfoProps {
  deliveryDate?: string;
  trackingNumber: string;
  carrier: string | null;
  trackingEvents: TrackingEvent[];
}

const copyTrackingNumber = (text: string) => {
  navigator.clipboard.writeText(text);
  copyToast('Tracking number copied');
};

export const OrderTrackingInfo = ({
  deliveryDate,
  trackingNumber,
  carrier,
  trackingEvents,
}: OrderTrackingInfoProps) => {
  const hasTrackingEvents = trackingEvents && trackingEvents.length > 0;
  const [isTimelineExpanded, setIsTimelineExpanded] = useState(hasTrackingEvents);

  const toggleTimeline = () => {
    setIsTimelineExpanded(!isTimelineExpanded);
  };

  return (
    <div className="flex flex-col gap-4 bg-neutral-50 rounded-[8px] p-3 w-full">
      <div className="flex justify-between items-start w-full">
        <div className="flex flex-col gap-3 items-start">
          {deliveryDate && (
            <div className="flex flex-col gap-[5px] items-start">
              <p className="text-label-small font-normal text-neutral-400">Delivery Date</p>
              <p className="text-label-medium font-medium text-neutral-700">{deliveryDate}</p>
            </div>
          )}
          <div className="flex flex-col gap-[5px] items-start">
            <div className="flex flex-row gap-1">
              <p className="text-label-small font-normal text-neutral-400">Tracking: {trackingNumber}</p>
              <div
                className="cursor-pointer hover:opacity-80 w-4 h-4 text-neutral-400"
                onClick={() => copyTrackingNumber(trackingNumber)}
              >
                <LucideIcon name="Copy" size={16} />
              </div>
            </div>
            {carrier && <p className="text-label-small font-normal text-neutral-400">Shipped via {carrier}</p>}
          </div>
        </div>
        {hasTrackingEvents && (
          <div className="flex flex-row gap-1 cursor-pointer hover:opacity-80 items-center" onClick={toggleTimeline}>
            <p className="text-label-small font-normal text-neutral-400">Track</p>
            <div className="w-4 h-4 text-neutral-400">
              <LucideIcon name={isTimelineExpanded ? 'CircleMinus' : 'CirclePlus'} size={16} />
            </div>
          </div>
        )}
      </div>

      {isTimelineExpanded && hasTrackingEvents && (
        <>
          <Separator className="w-full bg-neutral-100" />

          <TrackingTimeline events={trackingEvents} />
        </>
      )}
    </div>
  );
};
